// Google Drive API service for working with sheets using drive.file scope

class DriveService {
  constructor() {
    this.accessToken = null;
  }

  setAccessToken(token) {
    this.accessToken = token;
  }

  // Get sheet metadata using Drive API
  async getSheetMetadata(fileId) {
    if (!this.accessToken) {
      throw new Error("Access token not set");
    }

    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,mimeType,webViewLink`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Drive API error: ${response.status}`);
      }

      const data = await response.json();
      return {
        id: data.id,
        name: data.name,
        mimeType: data.mimeType,
        url: data.webViewLink,
      };
    } catch (error) {
      console.error("Error fetching sheet metadata:", error);
      throw error;
    }
  }

  // Check if user has permission to edit a sheet
  async checkSheetPermission(fileId) {
    if (!this.accessToken) {
      throw new Error("Access token not set");
    }

    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,capabilities`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          return {
            hasPermission: false,
            reason: "We can't access this file with current permissions.",
          };
        }
        if (response.status === 404) {
          return {
            hasPermission: false,
            reason: "We can't find this sheet with current permissions.",
          };
        }
        throw new Error(`Drive API error: ${response.status}`);
      }

      const data = await response.json();
      const canEdit = data.capabilities?.canEdit || false;

      return {
        hasPermission: canEdit,
        reason: canEdit
          ? "Has edit permission"
          : "No edit permission - you can view but not edit this file",
      };
    } catch (error) {
      console.error("Error checking sheet permission:", error);
      return {
        hasPermission: false,
        reason: `Error checking permissions: ${error.message}`,
      };
    }
  }

  // Create a new Google Sheet
  async createNewSheet(sheetName = "PageTest Metrics") {
    if (!this.accessToken) {
      throw new Error("Access token not set");
    }

    try {
      const response = await fetch(
        "https://sheets.googleapis.com/v4/spreadsheets",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            properties: {
              title: sheetName,
            },
            sheets: [
              {
                properties: {
                  title: "Sheet1",
                  gridProperties: {
                    rowCount: 1000,
                    columnCount: 20,
                  },
                },
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Sheets API error: ${response.status}`);
      }

      const data = await response.json();
      return {
        id: data.spreadsheetId,
        name: data.properties.title,
        url: `https://docs.google.com/spreadsheets/d/${data.spreadsheetId}`,
      };
    } catch (error) {
      console.error("Error creating new sheet:", error);
      throw error;
    }
  }

  // Get sheet tabs using Drive API (convert to Sheets API call)
  async getSheetTabs(fileId) {
    if (!this.accessToken) {
      throw new Error("Access token not set");
    }

    try {
      // Use Sheets API but with drive.file scope - this will only work for files
      // that the user has explicitly granted access to through the picker
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${fileId}?fields=sheets.properties.title`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Sheets API error: ${response.status}`);
      }

      const data = await response.json();
      return data.sheets.map((sheet) => sheet.properties.title);
    } catch (error) {
      console.error("Error fetching sheet tabs:", error);
      throw error;
    }
  }

  // Create a new tab in the sheet
  async createSheetTab(fileId, tabName) {
    if (!this.accessToken) {
      throw new Error("Access token not set");
    }

    try {
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${fileId}:batchUpdate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            requests: [
              {
                addSheet: {
                  properties: { title: tabName },
                },
              },
            ],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Sheets API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating sheet tab:", error);
      throw error;
    }
  }

  // Append data to a sheet
  async appendToSheet(fileId, tabName, values) {
    if (!this.accessToken) {
      throw new Error("Access token not set");
    }

    try {
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${fileId}/values/${tabName}!A1:append?valueInputOption=RAW`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            values: values,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Sheets API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error appending to sheet:", error);
      throw error;
    }
  }
}

// Export singleton instance
const driveService = new DriveService();
export default driveService;
